type Interval = ReturnType<typeof requestAnimationFrame>;

export type UseAnimationLoopContext = {
  time: number;
  delta: number;
  elapsed: number;
  stop: () => void;
}

export type UseAnimationLoopFunction = (ctx: UseAnimationLoopContext) => any;

export type UseAnimationLoopProps = {
  cancelPreviousOnStart?: boolean;
}
export type UseAnimationLoop = {
  start: (fun: UseAnimationLoopFunction) => void;
  stop: () => void;
  isRunning: Ref<boolean>;
}

export const useAnimationLoop = (props: UseAnimationLoopProps = {}): UseAnimationLoop => {
  const isRunning = ref<boolean>(false);
  
  let _isRunning: boolean = false;
  let _id: Interval | null = null;

  const updateIsRunning = (running: boolean) => {
    _isRunning = running;
    isRunning.value = running;
  }

  const stop = () => {
    if (_id !== null) {
      cancelAnimationFrame(_id);
      _id = null;
    }
    updateIsRunning(false);
  }

  const start = (fun: UseAnimationLoopFunction) => {
    if (_isRunning) {
      if (props.cancelPreviousOnStart) {
        stop();
      } else {
        return;
      }
    }

    _isRunning = true;
    setTimeout(() => {
      updateIsRunning(true);
    }, 0);

    let stopped: boolean = false;
    let prevTime: number = -1;
    let timeStarted: number = -1;
    const loop = (time: number) => {
      if (prevTime < 0) {
        prevTime = time;
        timeStarted = time;
      }

      const elapsed = time - timeStarted;
      const delta = time - prevTime;
      prevTime = time;
      
      const ctx: UseAnimationLoopContext = {
        time: time / 1000.0,
        delta: delta / 1000.0,
        elapsed: elapsed / 1000.0,
        stop: () => {
          stopped = true;
          stop();
        }
      };

      fun(ctx);
      
      if (stopped) return;
      _id = requestAnimationFrame(loop);
    }
    _id = requestAnimationFrame(loop);
  }

  return { start, stop, isRunning }
}
